import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, downloadPdf } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArtifactForm } from "../../components/ArtifactTable";
import { Plus, Pencil, Trash2, Link2, Upload, Flag, FileDown } from "lucide-react";

const PEOPLE_KEYS = [["assigneeIds", "Assignees (Accountable)", true], ["ownerId", "Owner", false], ["reviewerIds", "Reviewers", true], ["approverIds", "Approvers", true]];

const TASK_FIELDS = [
  { key: "wbsCode", label: "WBS Code", type: "text" },
  { key: "name", label: "Task Name", type: "text" },
  { key: "durationDays", label: "Duration (days)", type: "number" },
  { key: "plannedStart", label: "Planned Start", type: "date" },
  { key: "plannedFinish", label: "Planned Finish", type: "date" },
  { key: "percentComplete", label: "% Complete", type: "number" },
  { key: "status", label: "Status", type: "select", options: ["NotStarted", "InProgress", "Completed", "Blocked"] },
  { key: "isMilestone", label: "Milestone?", type: "select", options: ["true", "false"] },
  { key: "owner", label: "Owner", type: "text" },
];

const DEP_FIELDS_TYPES = ["FS", "SS", "FF", "SF"];

function daysBetween(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }

function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function SchedulePage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [depOpen, setDepOpen] = useState(false);
  const [depForm, setDepForm] = useState({ fromTaskId: "", toTaskId: "", type: "FS", lagDays: 0 });
  const [drag, setDrag] = useState(null);
  const [members, setMembers] = useState([]);
  const [zoom, setZoom] = useState("auto");

  useEffect(() => {
    api.get(`/projects/${projectId}/members`).then((r) => setMembers(r.data)).catch(() => {});
  }, [projectId]);

  const setBaseline = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/baseline`);
      toast.success(`Baseline captured for ${data.baselined} tasks`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importFile = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} tasks imported`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/schedule`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const range = useMemo(() => {
    if (!data?.tasks?.length) return null;
    const starts = data.tasks.map((t) => t.plannedStart || t.computedStart).filter(Boolean);
    const ends = data.tasks.map((t) => t.plannedFinish || t.computedFinish).filter(Boolean);
    if (!starts.length) return null;
    const min = starts.reduce((a, b) => (a < b ? a : b));
    const max = ends.reduce((a, b) => (a > b ? a : b));
    return { min, max, total: Math.max(daysBetween(min, max), 1) };
  }, [data]);

  const ticks = useMemo(() => {
    if (!range) return [];
    const total = range.total;
    const start = new Date(range.min + "T00:00:00Z");
    const end = new Date(range.max + "T00:00:00Z");
    const out = [];
    const push = (d, label, major) => {
      const pct = (daysBetween(range.min, d.toISOString().slice(0, 10)) / total) * 100;
      if (pct > 0.5 && pct < 99.5) out.push({ pct, label, major });
    };
    const unit = zoom !== "auto" ? zoom
      : total <= 14 ? "day" : total <= 100 ? "week" : total <= 550 ? "month" : "quarter";
    if (unit === "day") {
      const d = new Date(start); d.setUTCDate(d.getUTCDate() + 1);
      while (d <= end) { push(new Date(d), d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }), true); d.setUTCDate(d.getUTCDate() + 1); }
    } else if (unit === "week") {
      const d = new Date(start); d.setUTCDate(d.getUTCDate() + ((8 - d.getUTCDay()) % 7 || 7));
      while (d <= end) { push(new Date(d), d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }), true); d.setUTCDate(d.getUTCDate() + 7); }
    } else if (unit === "month") {
      const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
      while (d <= end) { push(new Date(d), d.toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" }), d.getUTCMonth() % 3 === 0); d.setUTCMonth(d.getUTCMonth() + 1); }
    } else {
      const d = new Date(Date.UTC(start.getUTCFullYear(), Math.floor(start.getUTCMonth() / 3) * 3 + 3, 1));
      while (d <= end) { push(new Date(d), `Q${Math.floor(d.getUTCMonth() / 3) + 1} '${String(d.getUTCFullYear()).slice(2)}`, true); d.setUTCMonth(d.getUTCMonth() + 3); }
    }
    return out;
  }, [range, zoom]);

  const axisWidth = useMemo(() => {
    if (!range) return 340;
    if (zoom === "week") return Math.max(340, Math.ceil(range.total / 7) * 56);
    if (zoom === "month") return Math.max(340, Math.ceil(range.total / 30) * 76);
    if (zoom === "quarter") return Math.max(340, Math.ceil(range.total / 91) * 90);
    return 340;
  }, [range, zoom]);

  const weekends = useMemo(() => {
    if (!range || !(zoom === "week" || range.total <= 60)) return [];
    const out = [];
    const d = new Date(range.min + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + ((6 - d.getUTCDay() + 7) % 7));
    while (daysBetween(range.min, d.toISOString().slice(0, 10)) < range.total) {
      const startPct = (daysBetween(range.min, d.toISOString().slice(0, 10)) / range.total) * 100;
      const widthPct = Math.min((2 / range.total) * 100, 100 - startPct);
      if (startPct < 100 && widthPct > 0) out.push({ pct: Math.max(startPct, 0), width: widthPct });
      d.setUTCDate(d.getUTCDate() + 7);
    }
    return out;
  }, [range, zoom]);

  const holidayBands = useMemo(() => {
    if (!range || !data?.holidays?.length) return [];
    return data.holidays.map((h) => {
      const off = daysBetween(range.min, h.date);
      if (off < 0 || off >= range.total) return null;
      return { pct: (off / range.total) * 100, width: Math.max((1 / range.total) * 100, 0.15), name: h.name };
    }).filter(Boolean);
  }, [range, data]);

  const labelEvery = ticks.length > 9 ? 2 : 1;
  const todayPct = useMemo(() => {
    if (!range) return null;
    const pct = (daysBetween(range.min, new Date().toISOString().slice(0, 10)) / range.total) * 100;
    return pct >= 0 && pct <= 100 ? pct : null;
  }, [range]);

  const save = async () => {
    try {
      const payload = { ...form, isMilestone: form.isMilestone === "true" || form.isMilestone === true, durationDays: Number(form.durationDays || 1), percentComplete: Number(form.percentComplete || 0) };
      if (editing) await api.patch(`/projects/${projectId}/tasks/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/tasks`, payload);
      setOpen(false); load(); toast.success("Task saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (t) => {
    try { await api.delete(`/projects/${projectId}/tasks/${t.id}`); load(); toast.success("Task deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveDep = async () => {
    try {
      await api.post(`/projects/${projectId}/dependencies`, { ...depForm, lagDays: Number(depForm.lagDays || 0) });
      setDepOpen(false); load(); toast.success("Dependency added");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startDrag = (e, t, mode) => {
    if (!range) return;
    e.preventDefault();
    e.stopPropagation();
    const track = e.currentTarget.closest(".gantt-track");
    if (!track) return;
    const pxPerDay = track.getBoundingClientRect().width / range.total;
    setDrag({ taskId: t.id, task: t, mode, startX: e.clientX, pxPerDay, deltaDays: 0 });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const deltaDays = Math.round((e.clientX - drag.startX) / drag.pxPerDay);
      setDrag((d) => (d ? { ...d, deltaDays } : d));
    };
    const onUp = async (e) => {
      const deltaDays = Math.round((e.clientX - drag.startX) / drag.pxPerDay);
      const t = drag.task;
      setDrag(null);
      if (!deltaDays) return;
      const start = t.plannedStart || t.computedStart;
      const finish = t.plannedFinish || t.computedFinish;
      try {
        if (drag.mode === "move") {
          await api.patch(`/projects/${projectId}/tasks/${t.id}`, {
            plannedStart: addDays(start, deltaDays),
            plannedFinish: addDays(finish, deltaDays),
          });
          toast.success(`"${t.name}" moved ${deltaDays > 0 ? "+" : ""}${deltaDays}d — critical path recalculated`);
        } else {
          const newFinish = addDays(finish, deltaDays);
          const dur = Math.max(daysBetween(start, newFinish), 1);
          if (newFinish < start) return;
          await api.patch(`/projects/${projectId}/tasks/${t.id}`, {
            plannedFinish: newFinish, durationDays: dur,
          });
          toast.success(`"${t.name}" duration set to ${dur}d — critical path recalculated`);
        }
        load();
      } catch (err) { toast.error(errMsg(err)); }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, projectId, load]);

  if (!data) return <div className="text-muted-foreground">Loading schedule…</div>;
  const tasks = data.tasks || [];

  return (
    <div data-testid="schedule-page">
      <PageHeader overline="Planning · Critical Path Method" title="WBS / Gantt / Critical Path"
        actions={
          <>
            <label className="cursor-pointer inline-flex items-center border border-border px-3 py-2 text-sm hover:border-[#1E6FE8]" data-testid="import-excel-btn">
              <Upload className="h-4 w-4 mr-1" />Excel
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && importFile("tasks-excel", e.target.files[0])} />
            </label>
            <label className="cursor-pointer inline-flex items-center border border-border px-3 py-2 text-sm hover:border-[#1E6FE8]" data-testid="import-msp-btn">
              <Upload className="h-4 w-4 mr-1" />MS Project
              <input type="file" accept=".xml" className="hidden" onChange={(e) => e.target.files[0] && importFile("ms-project", e.target.files[0])} />
            </label>
            <Button data-testid="export-gantt-btn" variant="outline" className="rounded-none"
              onClick={async () => {
                try { await downloadPdf(`/projects/${projectId}/export/gantt?unit=${zoom}`, "Gantt.pdf"); toast.success("Gantt PDF downloaded"); }
                catch (e) { toast.error(errMsg(e)); }
              }}>
              <FileDown className="h-4 w-4 mr-1" />PDF
            </Button>
            <Button data-testid="set-baseline-btn" variant="outline" className="rounded-none" onClick={setBaseline}>
              <Flag className="h-4 w-4 mr-1" />Set Baseline
            </Button>
            <Button data-testid="add-dependency-btn" variant="outline" className="rounded-none" onClick={() => { setDepForm({ fromTaskId: "", toTaskId: "", type: "FS", lagDays: 0 }); setDepOpen(true); }}>
              <Link2 className="h-4 w-4 mr-1" />Dependency
            </Button>
            <Button data-testid="add-task-btn" className="rounded-none" onClick={() => { setEditing(null); setForm({ status: "NotStarted", durationDays: 1, percentComplete: 0 }); setOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" />Add Task
            </Button>
          </>
        }>
        <div className="text-xs text-muted-foreground mt-1">
          {tasks.length} tasks · {data.criticalIds?.length || 0} on critical path · {data.projectStart} → {data.projectEnd}
          <span className="inline-flex items-center gap-1 ml-3"><span className="h-2 w-4 bg-[#FF3B30] inline-block" /> critical</span>
          <span className="inline-flex items-center gap-1 ml-2"><span className="h-2 w-4 bg-[#1E6FE8] inline-block" /> normal</span>
          <span className="inline-flex items-center gap-1 ml-2"><span className="h-[3px] w-4 bg-neutral-500 inline-block" /> baseline</span>
          <span className="ml-3 text-[#1E6FE8]">Drag bars to reschedule · drag right edge to resize</span>
        </div>
      </PageHeader>

      <div className="flex items-center justify-between mb-1.5">
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" title="When on, durations and the critical path are computed in working days (Mon–Fri). Turn off for projects that deploy over weekends.">
          <Switch data-testid="exclude-weekends-toggle" checked={data.excludeWeekends !== false}
            onCheckedChange={async (v) => {
              try {
                await api.patch(`/projects/${projectId}`, { excludeWeekends: v });
                toast.success(v ? "Weekends excluded — scheduling in working days (Mon–Fri)" : "Weekends included — scheduling in calendar days (weekend work allowed)");
                load();
              } catch (e) { toast.error(errMsg(e)); }
            }} />
          <span>Exclude weekends <span className="text-[10px]">(working-day scheduling)</span></span>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Timeline zoom</span>
          <div className="inline-flex border border-border" role="group">
            {[["auto", "Fit"], ["week", "Week"], ["month", "Month"], ["quarter", "Quarter"]].map(([v, l]) => (
              <button key={v} data-testid={`gantt-zoom-${v}`} onClick={() => setZoom(v)}
                className={`px-3 py-1 text-xs font-semibold ${zoom === v ? "bg-[#1E6FE8] text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {weekends.length > 0 && <span className="text-[10px] text-muted-foreground ml-1 inline-flex items-center gap-1"><span className="h-2.5 w-3 bg-neutral-300/70 inline-block" /> weekend</span>}
          {holidayBands.length > 0 && <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><span className="h-2.5 w-3 bg-amber-300/60 inline-block" /> holiday</span>}
        </div>
      </div>
      <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-240px)]">
        <table className="w-full data-table text-sm" style={{ minWidth: 760 + axisWidth }}>
          <thead>
            <tr>
              <th className="w-16">WBS</th><th style={{ minWidth: 240 }}>Task</th><th>Accountable</th><th>Dur</th>
              <th>Start</th><th>Finish</th><th>Float</th><th>Slip</th><th>%</th><th>Status</th>
              <th style={{ minWidth: axisWidth }}>
                <div className="relative h-7" data-testid="gantt-time-axis">
                  {ticks.map((tk, i) => (
                    <div key={i} className="absolute top-0 h-full" style={{ left: `${tk.pct}%` }}>
                      <div className="absolute bottom-0 h-1.5 w-px bg-neutral-400" />
                      {i % labelEvery === 0 && (
                        <span className="absolute bottom-2 -translate-x-1/2 text-[9px] font-medium normal-case tracking-normal text-neutral-500 whitespace-nowrap">{tk.label}</span>
                      )}
                    </div>
                  ))}
                  {todayPct != null && (
                    <span data-testid="gantt-today-marker" className="absolute bottom-0 -translate-x-1/2 text-[9px] font-bold text-[#1E6FE8]" style={{ left: `${todayPct}%` }} title="Today">▾</span>
                  )}
                </div>
              </th><th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => {
              const isParent = !t.parentId;
              const start = t.plannedStart || t.computedStart;
              const finish = t.plannedFinish || t.computedFinish;
              const left = range && start ? (daysBetween(range.min, start) / range.total) * 100 : 0;
              const width = range && start && finish ? Math.max((daysBetween(start, finish) / range.total) * 100, 0.8) : 0;
              const blStart = t.baselineStart, blFinish = t.baselineFinish;
              const blLeft = range && blStart ? (daysBetween(range.min, blStart) / range.total) * 100 : null;
              const blWidth = range && blStart && blFinish ? Math.max((daysBetween(blStart, blFinish) / range.total) * 100, 0.8) : null;
              const slip = blFinish && finish ? daysBetween(blFinish, finish) : null;
              const firstAssignee = (t.assigneeIds || [])[0];
              const accMember = members.find((m) => m.userId === firstAssignee) || members.find((m) => m.userId === t.ownerId);
              const acc = accMember?.name || t.owner || null;
              return (
                <tr key={t.id} data-testid="task-row" className={isParent ? "bg-neutral-100/70 font-semibold" : ""}>
                  <td className="font-mono2 text-xs">{t.wbsCode}</td>
                  <td className={(isParent ? "" : "pl-6") + " cursor-pointer hover:text-[#1E6FE8]"}
                    data-testid="task-name-cell"
                    onClick={() => { setEditing(t); setForm({ ...t, isMilestone: String(!!t.isMilestone) }); setOpen(true); }}>
                    {t.name}
                    {t.isMilestone && <span className="ml-1.5 text-[#1E6FE8]" title="Milestone">◆</span>}
                  </td>
                  <td className="whitespace-nowrap" data-testid="gantt-accountable">
                    {acc ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="h-5 w-5 rounded-full bg-[#1E6FE8] text-white text-[9px] font-bold flex items-center justify-center">
                          {acc.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </span>
                        <span className="text-xs">{acc}</span>
                      </span>
                    ) : <span className="text-muted-foreground text-xs">—</span>}
                  </td>
                  <td className="whitespace-nowrap">{t.durationDays}d</td>
                  <td className="whitespace-nowrap text-xs">{start}</td>
                  <td className="whitespace-nowrap text-xs">{finish}</td>
                  <td className="text-xs">{t.totalFloat != null ? `${t.totalFloat}d` : "—"}</td>
                  <td className="text-xs" data-testid="slip-cell">
                    {(() => {
                      const today = new Date().toISOString().slice(0, 10);
                      const done = (t.percentComplete || 0) >= 100 || t.status === "Completed";
                      const daysLate = finish && !done && finish < today ? daysBetween(finish, today) : 0;
                      if (daysLate > 0) {
                        return <span className="text-[#b3221a] font-semibold" title={`Finish date ${finish} has passed — ${daysLate}d overdue`}>
                          {slip != null && slip !== 0 ? `${slip > 0 ? "+" : ""}${slip}d · ` : ""}{daysLate}d late</span>;
                      }
                      return slip == null ? "—" : slip === 0 ? <span className="text-[#007a4f]">on plan</span>
                        : slip > 0 ? <span className="text-[#b3221a] font-semibold">+{slip}d</span>
                        : <span className="text-[#007a4f]">{slip}d</span>;
                    })()}
                  </td>
                  <td className="text-xs">{t.percentComplete || 0}%</td>
                  <td><StatusBadge value={t.status} /></td>
                  <td>
                    <div className="relative h-4 bg-neutral-100 w-full gantt-track">
                      {weekends.map((w, i) => (
                        <div key={`w${i}`} data-testid="gantt-weekend-band" className="absolute top-0 h-full pointer-events-none bg-neutral-300/40" style={{ left: `${w.pct}%`, width: `${w.width}%` }} />
                      ))}
                      {holidayBands.map((h, i) => (
                        <div key={`h${i}`} data-testid="gantt-holiday-band" title={h.name} className="absolute top-0 h-full pointer-events-none bg-amber-300/50" style={{ left: `${h.pct}%`, width: `${h.width}%` }} />
                      ))}
                      {ticks.map((tk, i) => (
                        <div key={i} className={`absolute top-0 h-full w-px pointer-events-none ${drag ? "bg-neutral-300" : "bg-neutral-200/80"}`} style={{ left: `${tk.pct}%` }} />
                      ))}
                      {todayPct != null && (
                        <div className="absolute top-0 h-full w-px pointer-events-none bg-[#1E6FE8]/40" style={{ left: `${todayPct}%` }} />
                      )}
                      {blLeft != null && !t.isMilestone && (
                        <div data-testid="baseline-bar" className="absolute bottom-0 h-[3px] bg-neutral-500/70"
                          style={{ left: `${blLeft}%`, width: `${blWidth}%` }} title={`Baseline: ${blStart} → ${blFinish}`} />
                      )}
                      {t.isMilestone ? (
                        <span data-testid="gantt-milestone" onPointerDown={(e) => startDrag(e, t, "move")}
                          className="absolute -top-0.5 text-[#1E6FE8] cursor-grab active:cursor-grabbing select-none touch-none"
                          style={{ left: `${left + (drag?.taskId === t.id ? (drag.deltaDays / range.total) * 100 : 0)}%` }}>◆</span>
                      ) : (() => {
                        const isDragging = drag?.taskId === t.id;
                        const deltaPct = isDragging && range ? (drag.deltaDays / range.total) * 100 : 0;
                        const barLeft = drag?.mode === "move" ? left + deltaPct : left;
                        const barWidth = drag?.mode === "resize" && isDragging ? Math.max(width + deltaPct, 0.8) : width;
                        return (
                          <div data-testid="gantt-bar" onPointerDown={(e) => startDrag(e, t, "move")}
                            className={`absolute top-0 h-[13px] cursor-grab active:cursor-grabbing select-none touch-none ${t.computedCritical ? "bg-[#FF3B30]" : "bg-[#1E6FE8]"} ${isDragging ? "opacity-80 ring-1 ring-black z-10" : ""}`}
                            style={{ left: `${barLeft}%`, width: `${barWidth}%` }}
                            title="Drag to move · drag right edge to resize">
                            <div className="h-full bg-black/25 pointer-events-none" style={{ width: `${t.percentComplete || 0}%` }} />
                            <div data-testid="gantt-resize-handle" onPointerDown={(e) => startDrag(e, t, "resize")}
                              className="absolute -right-0.5 top-0 h-full w-1 cursor-ew-resize bg-black/40 hover:bg-black/70" />
                            {isDragging && drag.deltaDays !== 0 && (
                              <span className="absolute -top-5 left-0 text-[10px] font-mono2 bg-black text-white px-1 whitespace-nowrap">
                                {drag.deltaDays > 0 ? "+" : ""}{drag.deltaDays}d
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    <button data-testid="edit-task-btn" onClick={() => { setEditing(t); setForm({ ...t, isMilestone: String(!!t.isMilestone) }); setOpen(true); }} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid="delete-task-btn" onClick={() => remove(t)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="border border-border bg-card mt-4">
        <div className="px-4 py-2.5 border-b border-border overline-label">Dependencies ({(data.dependencies || []).length})</div>
        <table className="w-full text-sm data-table">
          <thead><tr><th>Predecessor</th><th>Successor</th><th>Type</th><th>Lag</th><th></th></tr></thead>
          <tbody>
            {(data.dependencies || []).map((d) => {
              const from = tasks.find((t) => t.id === d.fromTaskId);
              const to = tasks.find((t) => t.id === d.toTaskId);
              return (
                <tr key={d.id}>
                  <td>{from ? `${from.wbsCode} ${from.name}` : d.fromTaskId}</td>
                  <td>{to ? `${to.wbsCode} ${to.name}` : d.toTaskId}</td>
                  <td><StatusBadge value={d.type} tone="blue" /></td>
                  <td>{d.lagDays}d</td>
                  <td><button data-testid="delete-dep-btn" onClick={async () => { await api.delete(`/projects/${projectId}/dependencies/${d.id}`); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Task" : "Add Task"}</DialogTitle></DialogHeader>
          <ArtifactForm fields={TASK_FIELDS} value={form} onChange={setForm} />
          <div className="grid grid-cols-2 gap-3 mt-2">
            {PEOPLE_KEYS.map(([key, label, multi]) => (
              <div key={key}>
                <label className="text-xs uppercase tracking-wide font-medium">{label}</label>
                <select data-testid={`field-${key}`} multiple={multi} size={multi ? 3 : undefined}
                  className="w-full border border-input px-2 py-1.5 text-sm bg-background mt-1"
                  value={multi ? (form[key] || []) : (form[key] || "")}
                  onChange={(e) => setForm({ ...form, [key]: multi ? Array.from(e.target.selectedOptions).map((o) => o.value) : e.target.value })}>
                  {!multi && <option value="">— unassigned —</option>}
                  {members.map((m) => <option key={m.userId} value={m.userId}>{m.name} ({m.role})</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <label className="text-xs uppercase tracking-wide font-medium">Parent (summary task)</label>
            <select data-testid="field-parentId" className="w-full border border-input px-3 py-2 text-sm bg-background mt-1" value={form.parentId || ""}
              onChange={(e) => setForm({ ...form, parentId: e.target.value || null })}>
              <option value="">— none (top level) —</option>
              {tasks.filter((t) => !t.parentId && t.id !== editing?.id).map((t) => <option key={t.id} value={t.id}>{t.wbsCode} {t.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-task-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={depOpen} onOpenChange={setDepOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">Add Dependency</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[["fromTaskId", "Predecessor"], ["toTaskId", "Successor"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs uppercase tracking-wide font-medium">{label}</label>
                <select data-testid={`dep-${k}`} className="w-full border border-input px-3 py-2 text-sm bg-background mt-1" value={depForm[k]}
                  onChange={(e) => setDepForm({ ...depForm, [k]: e.target.value })}>
                  <option value="">Select task…</option>
                  {tasks.map((t) => <option key={t.id} value={t.id}>{t.wbsCode} {t.name}</option>)}
                </select>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wide font-medium">Type</label>
                <select data-testid="dep-type" className="w-full border border-input px-3 py-2 text-sm bg-background mt-1" value={depForm.type}
                  onChange={(e) => setDepForm({ ...depForm, type: e.target.value })}>
                  {DEP_FIELDS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide font-medium">Lag (days)</label>
                <input data-testid="dep-lag" type="number" className="w-full border border-input px-3 py-2 text-sm bg-background mt-1" value={depForm.lagDays}
                  onChange={(e) => setDepForm({ ...depForm, lagDays: e.target.value })} />
              </div>
            </div>
            <Button data-testid="save-dep-btn" className="rounded-none w-full" onClick={saveDep} disabled={!depForm.fromTaskId || !depForm.toTaskId}>Add dependency</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
